import { useCallback, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import useFilters from '@ubeya/shared/hooks/account/useFilters';
import { HELP_DESK_VIEW_VALUES } from '@ubeya/shared/constants/helpDesk';
import { HELP_DESK_FILTERS_SLUGS } from '@ubeya/shared-web/constants';
import { isEmptyFilters } from '@ubeya/shared-web/utils/filters';
import { helpDeskFilters, helpDeskFiltersStorageKey } from 'shared/atoms/helpDesk';
import useHelpDeskFilterParams from '../useHelpDeskFilterParams';

const HELP_DESK_FILTERS_KEY = 'help-desk-tickets';

const toSavedFilters = ({ nextFilters, savedFilters, hiddenSlugs }) => {
  const isHidden = ({ slug }) => hiddenSlugs.includes(slug);
  const next = (nextFilters?.basic || []).map(({ slug, value }) => ({ slug, value }));
  const visible = next.filter((filter) => !isHidden(filter));
  const hidden = visible.length ? (savedFilters.basic || []).filter(isHidden) : [];

  return { basic: [...visible, ...hidden], fields: nextFilters?.fields || [] };
};

const useHelpDeskFilters = (view) => {
  const { accountId } = useAccount();
  const { data: { id: adminId } = {} } = useUser();
  const { filters: allFilters } = useFilters();

  const storageKey = useMemo(() => helpDeskFiltersStorageKey(accountId, adminId), [accountId, adminId]);
  const [savedFilters, setSavedFilters] = useRecoilState(helpDeskFilters(storageKey));

  const filterIdBySlug = useMemo(
    () => Object.fromEntries((allFilters?.[HELP_DESK_FILTERS_KEY]?.basic || []).map(({ id, slug }) => [slug, id])),
    [allFilters]
  );

  const disabledFilters = useMemo(
    () => (view === HELP_DESK_VIEW_VALUES.ASSIGNED_TO_ME ? [HELP_DESK_FILTERS_SLUGS.ASSIGNEES] : []),
    [view]
  );

  // Re-attach this session's option ids: Filter and FiltersRow both match a selection to its
  // option by id, so a slug-only filter would render no chip and could not be removed.
  const filters = useMemo(
    () => ({
      basic: (savedFilters.basic || [])
        .filter(({ slug }) => !disabledFilters.includes(slug))
        .map(({ slug, value }) => ({ id: filterIdBySlug[slug], slug, value }))
        .filter(({ id }) => id !== undefined),
      fields: savedFilters.fields || []
    }),
    [savedFilters, filterIdBySlug, disabledFilters]
  );

  const setFilters = useCallback(
    (nextFilters) => {
      const filtersToSave = toSavedFilters({ nextFilters, savedFilters, hiddenSlugs: disabledFilters });

      setSavedFilters(filtersToSave);

      if (!storageKey) {
        return;
      }

      try {
        localStorage.setItem(storageKey, JSON.stringify(filtersToSave));
      } catch (error) {
        // Storage blocked or quota exceeded — persisting filters must never break Help Desk.
      }
    },
    [savedFilters, disabledFilters, storageKey, setSavedFilters]
  );

  const filterParams = useHelpDeskFilterParams(filters);
  const noFilters = useMemo(() => isEmptyFilters(filters), [filters]);

  return { filters, setFilters, disabledFilters, filterParams, noFilters };
};

export default useHelpDeskFilters;
